import { useEffect, useRef, useState } from 'react';
import styles from './ccf-consumer-card-interactions-tab.module.scss';
import { CcfBox } from '@nice-ccf/ui-controls';
import CcfConsumerCardInteractionsTabHeader from './ccf-consumer-card-interactions-tab-header/ccf-consumer-card-interactions-tab-header';
import CcfConsumerCardInteractionsListView from './ccf-consumer-card-interactions-list-view/ccf-consumer-card-interactions-list-view';
import CcfConsumerCardInteractionsTableView from './ccf-consumer-card-interactions-table-view/ccf-consumer-card-interactions-table-view';
import { interactionsHistoryListData } from './mock-data/interactions-history-mock-data';
import CcfConsumerCardInteractionsDetailsView from './ccf-consumer-card-interactions-details-view/ccf-consumer-card-interactions-details-view';
import { useSelector } from 'react-redux';
import { activeContactSelector } from '../../../ccf-inbox/ccf-inbox.slice';
import {
  differenceInDays,
  isToday,
  isYesterday,
  parseISO,
  subDays,
  isWithinInterval,
} from 'date-fns';

/* eslint-disable-next-line */
export interface CcfConsumerCardInteractionsTabProps {}

/**
 * CcfConsumerCardInteractionsTab - returns Interactions Tab in consumer card
 * @param props - CcfConsumerCardInteractionsTabProps
 * @example - `<CcfConsumerCardInteractionsTab />`
 */
export function CcfConsumerCardInteractionsTab(
  props: CcfConsumerCardInteractionsTabProps
) {
  const activeContact = useSelector(activeContactSelector);
  const [selectedInteractionView, setSelectedInteractionView] =
    useState('List');
  const [previousInteractionView, setPreviousInteractionView] = useState('');
  const [interactionsHistoryData, setInteractionsHistoryData] = useState(
    interactionsHistoryListData
  );
  const [selectedInteraction, setSelectedInteraction] = useState<any>({});
  const interactionsHistoryLocalStorage = useRef(
    JSON.parse(localStorage.getItem('interactionHistoryLS') || '[]')
  );
  const [selectedFilterOption, setSelectedFilterOption] = useState('All');

  useEffect(() => {
    if (interactionsHistoryLocalStorage.current.length > 0) {
      setInteractionsHistoryData(interactionsHistoryLocalStorage.current);
    } else {
      localStorage.setItem(
        'interactionHistoryLS',
        JSON.stringify(interactionsHistoryListData)
      );
    }
  }, [interactionsHistoryLocalStorage.current]);

  /**
   * Function to select interaction view
   * @example interactionViewSelectHandler()
   */
  const interactionViewSelectHandler = (
    selectedView: string,
    interaction?: any
  ) => {
    setPreviousInteractionView(selectedInteractionView);
    setSelectedInteractionView(selectedView);
    setSelectedInteraction(interaction || {});
  };

  /**
   * Function to select interaction view
   * @example interactionViewSelectHandler()
   */
  const searchHandler = (searchQuery: string) => {
    if (searchQuery === '') {
      if (interactionsHistoryLocalStorage.current.length > 0) {
        setInteractionsHistoryData(interactionsHistoryLocalStorage.current);
      } else {
        setInteractionsHistoryData(interactionsHistoryListData);
      }
    } else {
      const modifiedInteractionsHistoryData = interactionsHistoryData.map(
        (interaction: any) => ({
          ...interaction,
          mediaType: interaction.media,
          contactName: `${activeContact?.customer.firstName} ${activeContact?.customer.lastName}`,
          contactId:
            interaction.media === 'Email'
              ? activeContact?.customer.emailId
              : activeContact?.customer.phoneNo,
        })
      );

      const filteredData = modifiedInteractionsHistoryData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(searchQuery)
        )
      );
      setInteractionsHistoryData(filteredData);
    }
  };

  /**@example */
  const filterDataByDate = (data: any, filterOption: any) => {
    const now = new Date();
    const filteredData = data.filter((item: any) => {
      const itemDate = parseISO(item.date);

      switch (filterOption) {
        case 'today':
          return isToday(itemDate);
        case 'yesterday':
          return isYesterday(itemDate);
        case 'lastWeek':
          return isWithinInterval(itemDate, {
            start: subDays(now, 7),
            end: now,
          });
        case 'lastMonth':
          return isWithinInterval(itemDate, {
            start: subDays(now, 30),
            end: now,
          });
        case 'last90Days':
          return isWithinInterval(itemDate, {
            start: subDays(now, 90),
            end: now,
          });
        default: // 'All'
          return true;
      }
    });

    return filteredData;
  };

  /**@example */
  const handleFilter = (option: any) => {
    setSelectedFilterOption(option);
    const dataSource =
      interactionsHistoryLocalStorage.current.length > 0
        ? interactionsHistoryLocalStorage.current
        : interactionsHistoryListData;
    const updatedData = filterDataByDate(dataSource, option);
    setInteractionsHistoryData(updatedData);
  };

  return (
    <CcfBox className={styles.interactionsTabContainer}>
      {selectedInteractionView !== 'Details' && (
        <CcfConsumerCardInteractionsTabHeader
          interactionViewSelectHandler={interactionViewSelectHandler}
          selectedInteractionView={selectedInteractionView}
          searchHandler={searchHandler}
          handleFilter={handleFilter}
        />
      )}

      <CcfBox className={styles.interactionHistory}>
        {selectedInteractionView === 'List' ? (
          <CcfConsumerCardInteractionsListView
            interactionsHistoryData={interactionsHistoryData}
            interactionViewSelectHandler={interactionViewSelectHandler}
          />
        ) : selectedInteractionView === 'Table' ? (
          <CcfConsumerCardInteractionsTableView
            interactionsHistoryData={interactionsHistoryData}
            interactionViewSelectHandler={interactionViewSelectHandler}
          />
        ) : selectedInteractionView === 'Details' ? (
          <CcfConsumerCardInteractionsDetailsView
            interaction={selectedInteraction}
            interactionViewSelectHandler={interactionViewSelectHandler}
            previousView={previousInteractionView}
          />
        ) : null}
      </CcfBox>
    </CcfBox>
  );
}

export default CcfConsumerCardInteractionsTab;
